/**
 * @ngdoc directive
 * @name ixTooltip
 * @restrict A
 * @module impactx.autoComplete
 *
 * @description
 * Attribute directive that shows a styled tooltip when hovering over an element.
 * Intended for use inside `md-autocomplete` dropdown entries, where `md-tooltip`
 * cannot be used because `md-autocomplete` uses `md-virtual-repeat` internally,
 * which recycles DOM nodes and causes stale/duplicate tooltip instances.
 *
 * The tooltip element is appended directly to `<body>` and positioned with
 * `position: fixed`, so it is never clipped by overflow-hidden ancestors.
 * It appears below the element by default and flips above if there is not
 * enough space below the viewport edge.
 *
 * Scroll events are captured in the capture phase so that any scrolling inside
 * nested containers (e.g. the virtual-repeat container) repositions the tooltip
 * correctly. `attrs.$observe` keeps the tooltip text in sync when virtual-repeat
 * reuses a DOM node for a different list entry while the tooltip is visible.
 *
 * Styling is controlled by the `.ix-tooltip` CSS class in `app.css`, which
 * matches the `.ixa-tooltip-wrapper` style used by the Angular `ixaTooltip`
 * directive for visual consistency across the application.
 *
 * @param {string} ixTooltip - The text to display in the tooltip.
 *   Pass an empty string to disable the tooltip without removing the directive.
 *
 * @example
 * <!-- Always show tooltip -->
 * <div ix-tooltip="Some long text">...</div>
 *
 * <!-- Conditionally show tooltip, controlled by showEntryTooltip on the parent scope -->
 * <div ix-tooltip="{{showEntryTooltip ? entry.value : ''}}">...</div>
 *
 * <!-- Via ix-async-autocomplete -->
 * <ix-async-autocomplete show-entry-tooltip="true" ...></ix-async-autocomplete>
 */
angular.module('impactx.autoComplete').directive('ixTooltip', function () {
    return {
        restrict: 'A',
        link: function (scope, element, attrs) {
            let tooltipEl = null;

            function positionTooltip() {
                if (!tooltipEl) return;
                let rect = element[0].getBoundingClientRect();
                let tipRect = tooltipEl.getBoundingClientRect();
                let margin = 8;

                let left = rect.left + rect.width / 2 - tipRect.width / 2;
                left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin));

                let top;
                if (window.innerHeight - rect.bottom >= tipRect.height + margin) {
                    top = rect.bottom + margin;
                } else {
                    top = rect.top - tipRect.height - margin;
                }

                tooltipEl.style.left = left + 'px';
                tooltipEl.style.top = top + 'px';
            }

            element.on('mouseenter', function () {
                let text = attrs.ixTooltip;
                if (!text) return;

                tooltipEl = document.createElement('div');
                tooltipEl.className = 'ix-tooltip';
                tooltipEl.textContent = text;
                document.body.appendChild(tooltipEl);
                positionTooltip();

                window.addEventListener('scroll', positionTooltip, true);
            });

            // Keep text and position in sync when virtual-repeat replaces the entry on this DOM node.
            attrs.$observe('ixTooltip', function (newText) {
                if (tooltipEl) {
                    tooltipEl.textContent = newText;
                    positionTooltip();
                }
            });

            element.on('mouseleave', function () {
                window.removeEventListener('scroll', positionTooltip, true);
                if (tooltipEl) {
                    document.body.removeChild(tooltipEl);
                    tooltipEl = null;
                }
            });

            scope.$on('$destroy', function () {
                window.removeEventListener('scroll', positionTooltip, true);
                if (tooltipEl) {
                    document.body.removeChild(tooltipEl);
                    tooltipEl = null;
                }
            });
        }
    };
});